import type {
  ContentDownloadItem,
  ContentDownloadItems,
  Contributor,
  ContributorNameComponents,
  CreateRefDataResponse,
  DownloadContentRequest,
  DownloadContentResponse,
  NewContributor,
  Publisher,
} from '@ilteducation/content-ref-data-types';
import { NewBookV1 } from '@ilteducation/content-registry-types';
import axios from 'axios';
import { ContentDownloadTypes } from '@ilteducation/content-ref-data-types/models/data-types';
import customProvider, { Params } from '@/data-provider/customProvider';

type Includes = {
  [param: string]: string;
};

export const getRefData = (resource: string, include?: Includes, cursor?: string): Promise<any> => {
  const params = {
    filter: {},
    sort: [],
    pagination: {
      cursor,
    },
    include: include || undefined,
  };

  return customProvider()
    .getList(resource, params)
    .then((result) => result.data);
};

export const getAllRefData = async (resource: string, include?: Includes): Promise<any> => {
  const params: Params = {
    filter: {},
    sort: [],
    pagination: {
      cursor: undefined,
      limit: 1000,
    },
    include: include || undefined,
  };

  const getData = () => customProvider().getList(resource, params);

  let res = await getData();
  let { cursor } = res;
  const { data } = res;

  while (cursor) {
    params.pagination.cursor = cursor;
    // eslint-disable-next-line no-await-in-loop
    res = await getData();
    cursor = res.cursor;
    if (res.data) {
      data!.push(...(res.data as any[]));
    }
  }
  return data;
};

export const getAvailablePublishers = () =>
  getAllRefData('publishers').then((publishers) =>
    publishers.map((pub: Publisher) => ({ name: pub.name, id: pub.id })),
  );

/** A suggestion list, not a browsable one - enough to pick from, not to page through. */
const CONTRIBUTOR_SEARCH_LIMIT = 10;

/**
 * Contributors by name. A query routes the list through the contributors search
 * endpoint, the same one the contributors list page searches on.
 */
export const searchContributors = async (query: string): Promise<Contributor[]> => {
  const { data } = await customProvider().getList('contributors', {
    filter: { q: query },
    sort: [],
    pagination: { limit: CONTRIBUTOR_SEARCH_LIMIT },
  });

  return (data ?? []) as Contributor[];
};

/** Creates a contributor and returns its id. Blank name components are dropped by the data provider. */
export const createContributor = async (name: ContributorNameComponents): Promise<string> => {
  const data: NewContributor = { isOrganization: false, name };
  const response = await customProvider().create('contributors', { data });
  return (response.data as CreateRefDataResponse).id;
};

export const getArticleTags = (): Promise<string[]> => {
  const url = process.env.VUE_APP_ARTICLES_AGGR_URL;
  return axios
    .get(`${url}?dim=tags&size=1000000`)
    .then((response) => {
      const aggregates: Array<{ field: string; counts: Array<{ key: string; count: number }> }> =
        response.data?.aggregates ?? [];
      const tagCounts = aggregates.find((a) => a.field === 'tags')?.counts ?? [];
      return tagCounts.map((t) => t.key).sort();
    });
};

export const getMetadata = (store: any, isbn: string): Promise<NewBookV1> =>
  store.dispatch('metadata/getOne', { id: isbn }).then((res: any) => {
    console.log('Metadata: ', res.data);
    return res.data;
  });

export const getOnyx = (store: any, isbn: string): Promise<any> =>
  store.dispatch('metadata/getOne', { id: `${isbn}?fields=all` }).then((res: any) => {
    console.log('Onyx: ', res.data);
    return res.data;
  });

export const getParsedMetadata = (store: any, toParse: any, format: string): Promise<NewBookV1> =>
  store
    .dispatch('parse/create', { data: toParse, queryParams: { format }, customHeaders: { 'Content-Type': 'text/xml' } })
    .then((res: any) => {
      console.log('Parsed metadata: ', res.data);
      return res.data;
    });

export const downloadExternalContent = async (
  isbn: string,
  type: ContentDownloadTypes,
  id?: string,
  preview = true,
  uploadToContentRegistry = false,
): Promise<DownloadContentResponse | undefined> => {
  const data: DownloadContentRequest = { contentId: id, isbn, type, preview, uploadToContentRegistry };

  try {
    const response = await axios.request({
      url: process.env.VUE_APP_DOWNLOADEXTERNALCONTENT_API_URL,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    });
    if (response.status >= 200 && response.status < 300) {
      return response.data as DownloadContentResponse;
    }
  } catch (error) {
    console.log(error);
  }
  return undefined;
};

export const listExternalContentDownloads = async (): Promise<ContentDownloadItem[] | undefined> => {
  try {
    const response = await axios.request({
      url: process.env.VUE_APP_LISTEXTERNALCONTENT_API_URL,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response.status >= 200 && response.status < 300) {
      return (response.data as ContentDownloadItems).items;
    }
  } catch (error) {
    console.log(error);
  }
  return undefined;
};

export const getExternalContentDownloadUrl = async (item: ContentDownloadItem): Promise<string | undefined> => {
  try {
    const data = {
      isbn: item.isbn,
      contentType: item.type,
    };
    const response = await axios.request({
      url: process.env.VUE_APP_GETCACHEDCONTENTDOWNLOADURL_API_URL,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    });
    if (response.status >= 200 && response.status < 300) {
      return (response.data as { url: string }).url;
    }
  } catch (error) {
    console.log(error);
  }
  return undefined;
};
